import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  HStack,
  Icon,
  Input,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MdAdd, MdCheckCircle, MdCloudUpload, MdDescription } from "react-icons/md";
import useTools from "../../Tools/useTools";
import ToolUserModal from "../../Tools/ToolUserModal";
import { fetchController } from "../../../utils/FetchController/fetchController";
import { getHostDomain } from "../../../utils/utils";

const TOOL_KEY_TO_PROVIDER = {
  hubspot: "hubspot",
  zoho: "zoho",
  gohighlevel: "leadconnector",
  google_calendar: "google_calendar",
  twilio_sms: "twilio",
  paypal: "paypal",
  freshdesk: "freshdesk",
  postgres_lookup: "postgres",
  modmed: "modmed",
};

const TOOL_LABELS = {
  hubspot: "HubSpot",
  zoho: "Zoho CRM",
  gohighlevel: "GoHighLevel",
  google_calendar: "Google Calendar",
  twilio_sms: "Twilio SMS",
  paypal: "PayPal",
  freshdesk: "Freshdesk",
  postgres_lookup: "Postgres",
  modmed: "ModMed FHIR",
};

function resolveToolKey(rawKey, crmChoice) {
  return rawKey === "crm_picker" ? crmChoice || rawKey : rawKey;
}

function ToolBindingCard({ toolKey, tool, value, onChange, onSetUpNew }) {
  const baseUrl = getHostDomain();
  const [linkUsers, setLinkUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!tool?.id) return;
    setLoading(true);
    try {
      const res = await fetchController(
        `${baseUrl}/api/v1/tool/${tool.id}/LinkToolUser?size=50`
      );
      setLinkUsers(res?.data?.items || []);
    } catch (e) {
      setLinkUsers([]);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, tool?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-pick the first connection if user hasn't picked yet — keeps
  // single-connection workspaces friction-free.
  useEffect(() => {
    if (!value && linkUsers.length === 1) onChange(linkUsers[0].id);
  }, [linkUsers, value, onChange]);

  const label = TOOL_LABELS[toolKey] || toolKey;
  const connected = linkUsers.length > 0;

  return (
    <Card borderWidth="1px" borderColor={connected ? "green.300" : "gray.200"}>
      <CardBody py={3}>
        <HStack justify="space-between" mb={connected ? 2 : 0}>
          <HStack>
            {connected ? (
              <Icon as={MdCheckCircle} color="green.500" boxSize={5} />
            ) : (
              <Box w={5} h={5} borderRadius="full" borderWidth="2px" borderColor="gray.300" />
            )}
            <Text fontWeight="medium">{label}</Text>
            {!tool && (
              <Badge colorScheme="red" fontSize="0.65rem">
                tool not found
              </Badge>
            )}
          </HStack>
          {tool && (
            <Button
              size="sm"
              variant="outline"
              leftIcon={<MdAdd />}
              onClick={() => onSetUpNew(tool, refresh)}
              isDisabled={loading}
            >
              {connected ? "Connect another" : "Set up"}
            </Button>
          )}
        </HStack>

        {connected && (
          <Box pl={7} pt={1}>
            {loading ? (
              <HStack fontSize="sm" color="gray.500">
                <Spinner size="xs" /> <Text>Loading connections...</Text>
              </HStack>
            ) : linkUsers.length === 1 ? (
              <Text fontSize="sm" color="gray.600">
                Using <strong>{linkUsers[0].name || "your connection"}</strong>
              </Text>
            ) : (
              <RadioGroup
                value={value || ""}
                onChange={onChange}
                size="sm"
              >
                <Stack spacing={1}>
                  {linkUsers.map((lu) => (
                    <Radio key={lu.id} value={lu.id}>
                      {lu.name || `Connection ${lu.id.slice(0, 8)}`}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            )}
          </Box>
        )}
      </CardBody>
    </Card>
  );
}

export default function Step5Connect({
  config,
  outcomeSelections,
  crmChoice,
  toolBindings,
  onToolBinding,
  knowledgeFiles,
  onFileSelected,
}) {
  const { toolsList, status: toolsStatus } = useTools();
  const fileInput = useRef(null);
  const [setUpTool, setSetUpTool] = useState(null);
  const refreshCardRef = useRef(null);

  const needed = useMemo(() => {
    if (!config) return [];
    const checked = (config.recommended_outcomes || []).filter(
      (o) => outcomeSelections[o.key]
    );
    const set = new Set();
    checked.forEach((o) => {
      if (o.tool_key) set.add(resolveToolKey(o.tool_key, crmChoice));
    });
    (config.recommended_tools || []).forEach((t) =>
      set.add(resolveToolKey(t.tool_key, crmChoice))
    );
    return [...set].filter(
      (k) =>
        k &&
        k !== "crm_picker" &&
        k !== "call_transfer" &&
        k !== "call_disconnect"
    );
  }, [config, outcomeSelections, crmChoice]);

  const findTool = (toolKey) => {
    const provider = TOOL_KEY_TO_PROVIDER[toolKey];
    if (!provider) return null;
    return toolsList.find((t) => t.tool_provider === provider) || null;
  };

  const openSetUpNew = (tool, refreshFn) => {
    refreshCardRef.current = refreshFn;
    // OAuth flows redirect the whole window away to the provider, then
    // bounce back to REACT_APP_REDIRECT_URI (= /tools). We mark the
    // wizard's intent in localStorage so /tools can ricochet the user
    // straight back to /build/from-template, with the ?code= preserved
    // for the modal to consume on the next mount. We also remember
    // WHICH tool was being set up so we can auto-reopen the same modal
    // when the user lands back on Step 5.
    window.localStorage.setItem(
      "insighto:wizard:oauth_return",
      "/build/from-template"
    );
    if (tool?.id) {
      window.localStorage.setItem("insighto:wizard:oauth_tool_id", tool.id);
    }
    setSetUpTool(tool);
  };

  const closeSetUp = () => {
    setSetUpTool(null);
    refreshCardRef.current?.();
    refreshCardRef.current = null;
    // Modal closed — clear OAuth state so we don't accidentally
    // ricochet a future tab from /tools, and strip the stale ?code= from
    // the URL once the modal has consumed it.
    window.localStorage.removeItem("insighto:wizard:oauth_return");
    window.localStorage.removeItem("insighto:wizard:oauth_tool_id");
    if (window.location.search) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  };

  // After the /tools ricochet, we land here with ?code=... in the URL
  // and the just-being-set-up tool id in localStorage. Auto-reopen the
  // same ToolUserModal so its AuthorizationBodyForm picks up the code
  // (it already reads ?code from the URL on mount).
  useEffect(() => {
    if (setUpTool) return;
    if (toolsList.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.get("code") && !params.get("error")) return;
    const pendingId = window.localStorage.getItem(
      "insighto:wizard:oauth_tool_id"
    );
    if (!pendingId) return;
    const tool = toolsList.find((t) => t.id === pendingId);
    if (tool) setSetUpTool(tool);
  }, [toolsList, setUpTool]);

  if (!config) return <Text>Pick a use case first.</Text>;

  return (
    <VStack spacing={6} align="stretch">
      {/* Hero illustration */}
      <Box mx="auto" w="160px" h="80px" mt={-2}>
        <svg viewBox="0 0 160 80" width="160" height="80">
          <defs>
            <linearGradient id="connectGrad" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0" stopColor="#3182CE" />
              <stop offset="1" stopColor="#7C3AED" />
            </linearGradient>
          </defs>
          <circle cx="30" cy="40" r="14" fill="url(#connectGrad)" opacity="0.85" />
          <circle cx="80" cy="20" r="14" fill="#fff" stroke="url(#connectGrad)" strokeWidth="2" />
          <circle cx="80" cy="60" r="14" fill="#fff" stroke="url(#connectGrad)" strokeWidth="2" />
          <circle cx="130" cy="40" r="14" fill="url(#connectGrad)" opacity="0.85" />
          <line x1="44" y1="40" x2="68" y2="24" stroke="#7C3AED" strokeWidth="2" />
          <line x1="44" y1="40" x2="68" y2="56" stroke="#7C3AED" strokeWidth="2" />
          <line x1="92" y1="24" x2="116" y2="40" stroke="#7C3AED" strokeWidth="2" />
          <line x1="92" y1="56" x2="116" y2="40" stroke="#7C3AED" strokeWidth="2" />
        </svg>
      </Box>

      <VStack spacing={1} textAlign="center">
        <Heading size="lg">Connect & upload</Heading>
        <Text color="gray.600">
          Set up each integration right here. If you have more than one
          connection per tool, pick which one to use.
        </Text>
      </VStack>

      {toolsStatus === "loading" ? (
        <HStack justify="center" py={6}>
          <Spinner size="sm" /> <Text color="gray.500">Loading tools...</Text>
        </HStack>
      ) : (
        <VStack spacing={3} align="stretch">
          {needed.length === 0 && (
            <Text color="gray.500" fontSize="sm" textAlign="center">
              No external tools needed for the outcomes you picked.
            </Text>
          )}
          {needed.map((toolKey) => (
            <ToolBindingCard
              key={toolKey}
              toolKey={toolKey}
              tool={findTool(toolKey)}
              value={toolBindings?.[toolKey] || ""}
              onChange={(toolUserId) => onToolBinding(toolKey, toolUserId)}
              onSetUpNew={openSetUpNew}
            />
          ))}
        </VStack>
      )}

      <Box
        borderWidth="2px"
        borderStyle="dashed"
        borderColor="gray.300"
        borderRadius="md"
        p={6}
      >
        <VStack spacing={3}>
          <Icon as={MdCloudUpload} boxSize={10} color="blue.400" />
          <Text fontWeight="medium">Add knowledge (optional)</Text>
          <Text fontSize="sm" color="gray.500" textAlign="center">
            Drop a PDF / DOCX with your menu, brochures, FAQs, or policies.
            We'll upload these when you click Deploy.
          </Text>
          <Input
            ref={fileInput}
            type="file"
            display="none"
            multiple
            accept=".pdf,.docx,.doc,.txt"
            onChange={(e) => onFileSelected(Array.from(e.target.files || []))}
          />
          <Button size="sm" onClick={() => fileInput.current?.click()}>
            Choose files
          </Button>
          {knowledgeFiles.length > 0 && (
            <VStack spacing={1} fontSize="sm" color="gray.700" w="full">
              {knowledgeFiles.map((f, i) => (
                <HStack key={i} spacing={2}>
                  <Icon as={MdDescription} color="blue.400" />
                  <Text>{f.name}</Text>
                  <Text color="gray.400" fontSize="xs">
                    ({Math.round(f.size / 1024)} KB)
                  </Text>
                </HStack>
              ))}
            </VStack>
          )}
        </VStack>
      </Box>

      {setUpTool && (
        <ToolUserModal isOpen={true} onClose={closeSetUp} tool={setUpTool} />
      )}
    </VStack>
  );
}
